import { useNavigate } from 'react-router-dom';
import { Menu, Bell, LogOut, ChevronDown, User, Settings, HelpCircle, Sun, Moon, Sunrise } from 'lucide-react';
import { useState, useEffect, useRef, ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';

interface HeaderProps {
  onMenuToggle: () => void;
}

interface Greeting {
  text: string;
  icon: ReactNode;
}

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function Header({ onMenuToggle }: HeaderProps) {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [greeting, setGreeting] = useState<Greeting>({ 
    text: '', 
    icon: <Sun className="h-5 w-5 text-amber-400 animate-spin-slow" />
  });
  const [error, setError] = useState<string | null>(null);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || 'Error al cerrar sesión');
      }
      
      return responseData;
    },
    onSuccess: () => {
      // Limpiar la sesión y redirigir
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      navigate('/login');
    },
    onError: (error: Error) => {
      setError(error.message);
      
      // Si el error es por sesión expirada, forzar el logout
      if (error.message.includes('sesión ha expirado')) {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        navigate('/login');
      }
    },
  });

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) {
        setGreeting({ 
          text: 'Buenos días', 
          icon: <Sun className="h-5 w-5 text-amber-400 animate-spin-slow" /> 
        });
      } else if (hour >= 12 && hour < 18) {
        setGreeting({ 
          text: 'Buenas tardes', 
          icon: <Sunrise className="h-5 w-5 text-orange-400 animate-pulse" /> 
        });
      } else {
        setGreeting({ 
          text: 'Buenas noches', 
          icon: <Moon className="h-5 w-5 text-blue-400 animate-bounce" /> 
        });
      }
    };

    updateGreeting();
    const interval = setInterval(updateGreeting, 60000); // Actualizar cada minuto
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700/50 px-4 sm:px-6">
      {error && (
        <div className="absolute top-0 right-0 m-4 p-4 bg-red-500 text-white rounded-lg shadow-lg">
          {error}
        </div>
      )}
      <div className="flex h-16 items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <button 
            className="md:hidden p-2 hover:bg-white/5 rounded-lg transition-colors" 
            onClick={onMenuToggle}
          >
            <Menu className="h-5 w-5 text-slate-300" />
          </button>

          {/* Greeting section */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5">
              {greeting.icon}
              <span className="text-slate-300 font-medium hidden sm:inline">
                {greeting.text},
              </span>
              <span className="text-white font-semibold">
                {userData.name?.split(' ')[0] || 'Usuario'}
              </span>
            </div>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <button className="relative p-2 hover:bg-white/5 rounded-lg transition-colors">
            <Bell className="h-5 w-5 text-slate-300" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary-500 rounded-full"></span>
          </button>

          {/* User menu */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 p-1.5 hover:bg-white/5 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-300">
                  {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-slate-200">
                  {userData.name || 'Usuario'}
                </div>
                <div className="text-xs text-slate-400">
                  {userData.email || 'usuario@email.com'}
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-1 z-20">
                <div className="px-4 py-2 border-b border-slate-700">
                  <p className="text-sm font-medium text-slate-200">{userData.name}</p>
                  <p className="text-xs text-slate-400">{userData.email}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/profile');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    Mi Perfil
                  </button>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/help');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Ayuda
                  </button>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configuración
                  </button>
                </div>
                <div className="border-t border-slate-700 py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-slate-700/50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
} 